import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"
import { AnalysisPage } from "~/components/design-system/pages"
import { Label } from "~/components/reusable/Label"
import Select from "~/components/reusable/Select"
import { ModuleSummaryCard } from "~/components/internal/user-activity/ModuleSummaryCard"
import { ActivityByHourDayHeatmap } from "~/components/internal/user-activity/ActivityByHourDayHeatmap"
import { CompanyActivityCard } from "~/components/internal/user-activity/CompanyActivityCard"
import { FeatureAdoptionMatrix } from "~/components/internal/user-activity/FeatureAdoptionMatrix"
import { api } from "~/utils/api"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"

export const Route = createFileRoute("/_auth/internal/customer-success/_layout/user-activity")({
  component: RouteComponent,
})

const timeRangeOptions = [
  { label: "Last 12 hours", value: 12 },
  { label: "Last 24 hours", value: 24 },
  { label: "Last 48 hours", value: 48 },
  { label: "Last 7 days", value: 168 },
]

function RouteComponent() {
  const [timeRange, setTimeRange] = useState(timeRangeOptions[1]!)

  const { data, isLoading, error } = api.amplitude.getPageViewActivity.useQuery(
    { hoursBack: timeRange.value },
    { refetchInterval: 60000 * 2 /* 2 min */ },
  )

  // Always fetch 7 days for the Hour/Day heatmap (needs full week coverage)
  const { data: weeklyData } = api.amplitude.getPageViewActivity.useQuery(
    { hoursBack: 168 },
    {
      refetchInterval: 60000 * 5,
      staleTime: 1000 * 60 * 5,
    },
  )

  const { data: allKnownPages } = api.amplitude.getAllKnownPages.useQuery()

  if (isLoading) {
    return <LoadingOverlay />
  }

  if (error) {
    return <div className="p-4 text-red-500">Error loading page view data: {error.message}</div>
  }

  return (
    <AnalysisPage>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row items-end gap-4">
          <div className="flex flex-col gap-2">
            <Label className="font-bold">Time Range</Label>
            <Select
              options={timeRangeOptions}
              value={timeRange}
              onChange={(value) => value && setTimeRange(value)}
            />
          </div>
          {data && (
            <div className="flex flex-row gap-6 text-fg-subtle">
              <span>
                <strong>{data.totalPageViews}</strong> total page views
              </span>
              <span>
                <strong>{data.uniquePages}</strong> unique pages
              </span>
              {allKnownPages && (
                <span>
                  <strong>{allKnownPages.length}</strong> known pages (last 30 days)
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <FeatureAdoptionMatrix companyModuleActivity={data?.companyModuleActivity ?? []} />
          <ModuleSummaryCard pageActivity={data?.pageActivity ?? []} />
          <ActivityByHourDayHeatmap activityByHourDay={weeklyData?.activityByHourDay ?? []} />
          <CompanyActivityCard companyActivity={data?.companyActivity ?? []} />
        </div>
      </div>
    </AnalysisPage>
  )
}
